import React, { useEffect, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCompass } from 'react-icons/fi';
import { useTutorialContext } from '../../contexts/TutorialContext';
import './css/TutorialOverlay.css';

const SPOTLIGHT_PADDING = 10; // px padding around the highlighted element
const TOOLTIP_WIDTH = 360;
const TOOLTIP_WIDTH_WIDE = 500; // wider variant for top-placement when space is tight
const TOOLTIP_MIN_HEIGHT_ESTIMATE = 180;
// Space above the target (px) below which the tooltip switches to a wider, shorter shape
const TOP_WIDE_THRESHOLD = 260;
const TOP_GAP = 24; // gap between tooltip bottom edge and target top edge

/**
 * Measure the bounding rect of the element with data-tutorial-id="targetId",
 * expanded by SPOTLIGHT_PADDING on all sides.
 * Returns null if the element doesn't exist in the DOM.
 */
const measureTarget = (targetId) => {
  if (!targetId) return null;
  const el = document.querySelector(`[data-tutorial-id="${targetId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return {
    top: r.top - SPOTLIGHT_PADDING,
    left: r.left - SPOTLIGHT_PADDING,
    width: r.width + SPOTLIGHT_PADDING * 2,
    height: r.height + SPOTLIGHT_PADDING * 2,
    bottom: r.bottom + SPOTLIGHT_PADDING,
    right: r.right + SPOTLIGHT_PADDING,
  };
};

/**
 * Compute tooltip style (position) based on available space around the spotlight rect.
 * Falls back to centered when rect is null or no room exists.
 */
const computeTooltipStyle = (rect, preferredPlacement) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    // Full-screen centered modal — the transform is baked into className, not style
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: `${TOOLTIP_WIDTH}px`,
      maxWidth: 'calc(100vw - 24px)',
    };
  }

  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = vw - rect.right;
  const spaceLeft = rect.left;

  const hasRoomBelow = spaceBelow >= TOOLTIP_MIN_HEIGHT_ESTIMATE;
  const hasRoomAbove = spaceAbove >= TOOLTIP_MIN_HEIGHT_ESTIMATE;
  const hasRoomRight = spaceRight >= TOOLTIP_WIDTH + 20;
  const hasRoomLeft = spaceLeft >= TOOLTIP_WIDTH + 20;

  // 'center' is kept as-is — handled in the switch to render centered on screen
  // while still spotlighting the target element.
  // For directional placements, fall back gracefully if there isn't enough room.
  let placement = preferredPlacement;
  if (placement === 'center') {
    // intentional center — skip directional resolution
  } else if (!placement) {
    // Auto: prefer bottom, then try other sides, last resort = center
    placement = hasRoomBelow ? 'bottom'
      : hasRoomAbove ? 'top'
      : hasRoomRight ? 'right'
      : hasRoomLeft ? 'left'
      : 'center';
  } else if (placement === 'bottom' && !hasRoomBelow) {
    placement = hasRoomAbove ? 'top' : hasRoomRight ? 'right' : hasRoomLeft ? 'left' : 'center';
  } else if (placement === 'top' && !hasRoomAbove) {
    placement = hasRoomBelow ? 'bottom' : hasRoomRight ? 'right' : hasRoomLeft ? 'left' : 'center';
  } else if (placement === 'right' && !hasRoomRight) {
    placement = hasRoomLeft ? 'left' : hasRoomBelow ? 'bottom' : hasRoomAbove ? 'top' : 'center';
  } else if (placement === 'left' && !hasRoomLeft) {
    placement = hasRoomRight ? 'right' : hasRoomBelow ? 'bottom' : hasRoomAbove ? 'top' : 'center';
  }

  const centerX = rect.left + rect.width / 2;
  // Helper: left offset clamped within the viewport for a given tooltip width
  const leftFor = (w) => Math.max(12, Math.min(centerX - w / 2, vw - w - 12));
  const clampedTop = (rawTop) => Math.max(12, Math.min(rawTop, vh - TOOLTIP_MIN_HEIGHT_ESTIMATE - 12));

  const base = {
    position: 'fixed',
    maxWidth: 'calc(100vw - 24px)',
  };

  switch (placement) {
    case 'bottom':
      return {
        ...base,
        width: `${TOOLTIP_WIDTH}px`,
        top: `${clampedTop(rect.bottom + 16)}px`,
        left: `${leftFor(TOOLTIP_WIDTH)}px`,
      };

    case 'top': {
      // Anchor the tooltip's *bottom* edge just above the target so it always grows
      // upward — no risk of overlapping the element regardless of tooltip height.
      const tooltipBottom = rect.top - TOP_GAP; // px from viewport top to tooltip bottom
      const availableHeight = tooltipBottom - 12; // room between viewport top margin and target

      // Switch to wider/shorter shape when vertical space is tight.
      const useWide = availableHeight < TOP_WIDE_THRESHOLD;
      const w = useWide ? Math.min(TOOLTIP_WIDTH_WIDE, vw - 24) : TOOLTIP_WIDTH;
      const maxH = Math.max(120, availableHeight);

      return {
        ...base,
        width: `${w}px`,
        maxHeight: `${maxH}px`,
        overflowY: 'auto',
        // `bottom` anchors the lower edge of the box, so tooltip grows upward naturally.
        bottom: `${vh - tooltipBottom}px`,
        left: `${leftFor(w)}px`,
      };
    }

    case 'right':
      return {
        ...base,
        width: `${TOOLTIP_WIDTH}px`,
        top: `${clampedTop(rect.top)}px`,
        left: `${rect.right + 16}px`,
      };
    case 'left':
      return {
        ...base,
        width: `${TOOLTIP_WIDTH}px`,
        top: `${clampedTop(rect.top)}px`,
        right: `${vw - rect.left + 16}px`,
      };
    case 'center':
    default:
      // Centered on screen — spotlights the target but keeps the tooltip out of the way.
      return {
        ...base,
        width: `${TOOLTIP_WIDTH}px`,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
  }
};

const TutorialOverlay = () => {
  const { isActive, currentStep, activeStepIndex, totalSteps, nextStep, prevStep, skipTour } =
    useTutorialContext();

  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});

  // Recalculate positions whenever the active step changes, or on resize/scroll.
  // useLayoutEffect so positions are ready before paint.
  const recalculate = () => {
    if (!isActive || !currentStep) {
      setSpotlightRect(null);
      return;
    }
    const rect = measureTarget(currentStep.targetId);
    setSpotlightRect(rect);
    setTooltipStyle(computeTooltipStyle(rect, currentStep.placement));
  };

  useLayoutEffect(() => {
    if (!isActive) return;

    // First immediate calculation
    recalculate();

    // Re-measure after a tick (some elements need one paint cycle to appear)
    const rafId = requestAnimationFrame(recalculate);

    window.addEventListener('resize', recalculate);
    window.addEventListener('scroll', recalculate, { passive: true, capture: true });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('scroll', recalculate, { capture: true });
    };
  }, [isActive, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prevent body scroll during tour
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isActive]);

  if (!isActive || !currentStep) return null;
  if (typeof document === 'undefined') return null;

  // Centered when there's no target element, or when the step explicitly requests it.
  // In the explicit-center case the spotlight still renders around the target, but
  // the tooltip sits in the middle of the screen.
  const isCentered = !spotlightRect || currentStep.placement === 'center';
  // noScrim steps show only the tooltip — no dark overlay and no spotlight ring.
  // Use this when the target is itself a modal/dialog that must remain fully visible.
  const noScrim = Boolean(currentStep.noScrim);
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const scrimStyle = { background: 'rgba(9, 18, 38, 0.72)' };

  const overlayContent = (
    <div className="tutorial-overlay-root" role="dialog" aria-modal="true" aria-label="Product tour">
      {!noScrim && (
        isCentered ? (
          // Full-screen scrim when no spotlight target
          <div className="tutorial-scrim-panel" style={{ inset: 0, ...scrimStyle }} />
        ) : (
          // Four-panel spotlight cutout
          <>
            {/* Top */}
            <div
              className="tutorial-scrim-panel"
              style={{ top: 0, left: 0, right: 0, height: `${spotlightRect.top}px`, ...scrimStyle }}
            />
            {/* Bottom */}
            <div
              className="tutorial-scrim-panel"
              style={{
                top: `${spotlightRect.bottom}px`,
                left: 0,
                right: 0,
                bottom: 0,
                ...scrimStyle,
              }}
            />
            {/* Left */}
            <div
              className="tutorial-scrim-panel"
              style={{
                top: `${spotlightRect.top}px`,
                left: 0,
                width: `${spotlightRect.left}px`,
                height: `${spotlightRect.height}px`,
                ...scrimStyle,
              }}
            />
            {/* Right */}
            <div
              className="tutorial-scrim-panel"
              style={{
                top: `${spotlightRect.top}px`,
                left: `${spotlightRect.right}px`,
                right: 0,
                height: `${spotlightRect.height}px`,
                ...scrimStyle,
              }}
            />
            {/* Highlight ring over the spotlight hole */}
            <div
              className="tutorial-spotlight-ring"
              style={{
                top: `${spotlightRect.top}px`,
                left: `${spotlightRect.left}px`,
                width: `${spotlightRect.width}px`,
                height: `${spotlightRect.height}px`,
              }}
            />
          </>
        )
      )}

      {/* Tooltip card */}
      <div
        className={`tutorial-tooltip${isCentered ? ' tutorial-tooltip-centered' : ''}`}
        style={tooltipStyle}
        role="region"
        aria-live="polite"
      >
        {/* Header */}
        <div className="tutorial-tooltip-header">
          <div className="tutorial-tooltip-badge">
            <FiCompass />
          </div>
          <h3 className="tutorial-tooltip-title">{currentStep.title}</h3>
        </div>

        {/* Description */}
        {currentStep.description && (
          <p className="tutorial-tooltip-description">{currentStep.description}</p>
        )}

        {/* Note */}
        {currentStep.note && (
          <p className="tutorial-tooltip-note">{currentStep.note}</p>
        )}

        {/* Footer */}
        <div className="tutorial-tooltip-footer">
          {/* Progress dots */}
          <div className="tutorial-progress-dots" aria-label={`Step ${activeStepIndex + 1} of ${totalSteps}`}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`tutorial-progress-dot${i === activeStepIndex ? ' active' : ''}`}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="tutorial-tooltip-actions">
            <button className="tutorial-btn-skip" onClick={skipTour} type="button">
              Skip tour
            </button>
            {activeStepIndex > 0 && (
              <button className="tutorial-btn-prev" onClick={prevStep} type="button">
                Back
              </button>
            )}
            <button className="tutorial-btn-next" onClick={nextStep} type="button">
              {activeStepIndex === totalSteps - 1 ? 'Finish' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlayContent, document.body);
};

export default TutorialOverlay;
